/**
 * Лайтбокс с быстрым превью профиля участника — TG-стиль.
 * Открывается по клику на аватар/имя в чате; «Открыть полный профиль» уводит на /profile/$userId.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { UserMediaModal } from "@/components/UserMediaModal";
import { fetchUserPublic, fetchActiveUsers } from "@/api/dashboard";
import { orgsLine } from "@/api/orgs";
import { fmtLastSeen, isOnline } from "@/lib/presence";


/** Парсит @mentions в bio: если slug резолвится в активного юзера —
 * рендерим кликабельный <button> (открывает следующий лайтбокс). */
function renderBio(
  text: string,
  mentionMap: Record<string, string>,
  onMentionClick: (id: string) => void,
): ReactNode[] {
  const re = /@[\wа-яёА-ЯЁ_-]{2,80}|https?:\/\/[^\s<>"']+/giu;
  const parts: ReactNode[] = [];
  let last = 0; let i = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) parts.push(text.slice(last, start));
    const tok = m[0];
    if (tok.startsWith("@")) {
      const slug = tok.slice(1).toLowerCase();
      const uid = mentionMap[slug];
      if (uid) {
        parts.push(
          <button key={`m-${i++}`} type="button" className="chat-mention chat-mention-clickable"
            onClick={(e) => { e.stopPropagation(); onMentionClick(uid); }}>{tok}</button>
        );
      } else {
        parts.push(<span key={`m-${i++}`} className="chat-mention">{tok}</span>);
      }
    } else {
      parts.push(<a key={`u-${i++}`} className="chat-link" href={tok} target="_blank" rel="noopener noreferrer">{tok}</a>);
    }
    last = start + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}



const DEPARTMENT_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
  supply: "Отдел снабжения",
};


function roleLabel(role: string, isSuperuser: boolean = false): string {
  // Собственник + админ права = "Собственник / Администратор" (полный доступ).
  // У обычного admin'a без superuser-флага — просто "Администратор".
  if (isSuperuser) return "Собственник / Администратор";
  if (role === "owner") return "Собственник";
  if (role === "admin") return "Администратор";
  if (role === "head_op") return "Руководитель ОП";
  if (role === "op_manager") return "Менеджер ОП";
  if (role === "kc_manager") return "Менеджер КЦ";
  if (role === "assistant") return "Ассистент";
  if (role === "field_engineer") return "Выездной инженер";
  if (role === "engineer") return "Инженер";
  if (role === "foreman") return "Бригадир";
  if (role === "builder") return "Строитель";
  return role || "Участник";
}


export function ProfileLightbox({ userId, onClose, myUserId, onOpenDm }: {
  userId: string;
  onClose: () => void;
  myUserId?: string;
  onOpenDm?: (partnerUserId: string) => void;
}) {
  // viewedId меняется когда юзер кликнул @mention внутри bio — лайтбокс
  // переключается на нового участника без закрытия. История переходов
  // не ведётся (просто replace), Esc/X закрывает текущий.
  const [viewedId, setViewedId] = useState(userId);
  useEffect(() => { setViewedId(userId); }, [userId]);
  // Какой kind показывать в media-modal (null = модал закрыт)
  const [mediaKind, setMediaKind] = useState<"photo" | "file" | "voice" | "link" | null>(null);

  const q = useQuery({
    queryKey: ["profiles", viewedId],
    queryFn: () => fetchUserPublic(viewedId),
    refetchInterval: 30000,
  });
  // Карта slug→user_id для @mentions в bio
  const usersQ = useQuery({
    queryKey: ["chat/users-active"],
    queryFn: () => fetchActiveUsers(),
    staleTime: 60_000,
  });
  const mentionMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of usersQ.data ?? []) {
      if (!u.id || !u.display_name) continue;
      const slug = u.display_name.trim().toLowerCase().replace(/\s+/g, "_");
      if (slug) map[slug] = u.id;
    }
    return map;
  }, [usersQ.data]);

  // Esc — закрыть; блокируем скролл фона
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const profile = q.data;
  const online = profile ? isOnline(profile.last_seen_at) : false;

  return (
    <div
      className="profile-lightbox-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="profile-lightbox"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="profile-lightbox-close"
          onClick={onClose}
          title="Закрыть (Esc)"
        >
          <Icon name="x" size={18}/>
        </button>

        {q.isLoading && (
          <div style={{ padding: 60, display: "grid", placeItems: "center" }}>
            <Spinner size="md"/>
          </div>
        )}
        {q.isError && (
          <div style={{ padding: 40, color: "var(--brick)", textAlign: "center" }}>
            Не удалось загрузить профиль
          </div>
        )}

        {profile && (
          <>
            {/* Шапка: бэкграунд + большой аватар + имя + last_seen */}
            <div className="profile-lightbox-head">
              <div className="profile-lightbox-avatar-wrap">
                <Avatar
                  name={profile.display_name || "Участник"}
                  userId={profile.id}
                  avatarFilename={profile.avatar_filename}
                  color="#4A413B"
                  size={120}
                  online={online}
                />
              </div>
              <div className="profile-lightbox-name">
                {profile.display_name || "Без имени"}
              </div>
              <div className={`profile-lightbox-status${online ? " is-online" : ""}`}>
                {fmtLastSeen(profile.last_seen_at)}
              </div>
              {onOpenDm && myUserId && profile.id !== myUserId && (
                <button
                  type="button"
                  onClick={() => onOpenDm(profile.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    margin: "16px auto 0",
                    padding: "10px 20px",
                    background: "var(--brick)", color: "#fff",
                    border: "none", borderRadius: 8,
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}
                  title="Открыть личный чат"
                >
                  <Icon name="message" size={16}/>
                  Личные сообщения
                </button>
              )}
            </div>

            {/* Поля профиля */}
            <div className="profile-lightbox-body">
              {profile.role !== "external" && (
                <ProfileRow icon="user" label="Должность" value={roleLabel(profile.role, profile.is_superuser)}/>
              )}
              {(profile.orgs?.length ?? 0) > 0 && (
                <ProfileRow icon="users" label="Организации" value={orgsLine(profile.orgs)}/>
              )}
              {profile.position && profile.position.trim() !== roleLabel(profile.role, profile.is_superuser) && (
                <ProfileRow icon="tag" label="В команде" value={profile.position}/>
              )}
              {profile.departments && profile.departments.length > 0 && (
                <div className="profile-lightbox-row">
                  <span className="profile-lightbox-row-icon"><Icon name="folder" size={16}/></span>
                  <div className="profile-lightbox-row-text">
                    <div className="profile-lightbox-row-label">Отделы</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                      {profile.departments.map(d => DEPARTMENT_LABELS[d] && (
                        <span key={d} className={`dept-tag dept-tag-${d}`}>{DEPARTMENT_LABELS[d]}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {profile.birthday && (
                <ProfileRow icon="calendar" label="День рождения" value={fmtBirthday(profile.birthday)}/>
              )}
              {profile.bio && profile.bio.trim() && (
                <div className="profile-lightbox-row">
                  <span className="profile-lightbox-row-icon"><Icon name="info" size={16}/></span>
                  <div className="profile-lightbox-row-text">
                    <div className="profile-lightbox-row-label">О себе</div>
                    <div className="profile-lightbox-row-value" style={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
                      {renderBio(profile.bio, mentionMap, setViewedId)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Статистика медиа в чате — кликабельно */}
            {profile.stats && (
              <div className="profile-lightbox-stats">
                <StatRow icon="image" label="фото"        n={profile.stats.photos}         onClick={() => profile.stats!.photos > 0 && setMediaKind("photo")}/>
                <StatRow icon="file"  label="файлов"      n={profile.stats.files}          onClick={() => profile.stats!.files > 0 && setMediaKind("file")}/>
                <StatRow icon="mic"   label="голосовых"   n={profile.stats.voice_messages} onClick={() => profile.stats!.voice_messages > 0 && setMediaKind("voice")}/>
                <StatRow icon="link"  label="ссылок"      n={profile.stats.links}          onClick={() => profile.stats!.links > 0 && setMediaKind("link")}/>
              </div>
            )}
          </>
        )}
      </div>

      {mediaKind && profile && (
        <UserMediaModal
          userId={profile.id}
          kind={mediaKind}
          userName={profile.display_name || "Участник"}
          onClose={() => setMediaKind(null)}
          onNavigateAway={onClose}
        />
      )}
    </div>
  );
}


function ProfileRow({ icon, label, value, multiline }: {
  icon: string; label: string; value: string; multiline?: boolean;
}) {
  return (
    <div className="profile-lightbox-row">
      <span className="profile-lightbox-row-icon">
        <Icon name={icon} size={16}/>
      </span>
      <div className="profile-lightbox-row-text">
        <div className="profile-lightbox-row-label">{label}</div>
        <div
          className="profile-lightbox-row-value"
          style={multiline ? { whiteSpace: "pre-wrap", lineHeight: 1.45 } : undefined}
        >
          {value}
        </div>
      </div>
    </div>
  );
}


function StatRow({ icon, label, n, onClick }: {
  icon: string; label: string; n: number; onClick?: () => void;
}) {
  const clickable = !!onClick && n > 0;
  return (
    <button
      type="button"
      className={`profile-lightbox-stat${clickable ? " is-clickable" : ""}`}
      onClick={onClick}
      disabled={!clickable}
    >
      <span className="profile-lightbox-stat-icon">
        <Icon name={icon} size={18}/>
      </span>
      <span className="profile-lightbox-stat-num">{n.toLocaleString("ru-RU")}</span>
      <span className="profile-lightbox-stat-label">{label}</span>
      {clickable && (
        <span className="profile-lightbox-stat-arrow">
          <Icon name="chevron_r" size={14}/>
        </span>
      )}
    </button>
  );
}


/** "01-18" → "18 января". Год не показываем — хранится без года. */
function fmtBirthday(mmdd: string): string {
  const months = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];
  const m = mmdd.match(/^(\d{2})-(\d{2})$/);
  if (!m) return mmdd;
  const month = parseInt(m[1], 10) - 1;
  const day = parseInt(m[2], 10);
  if (month < 0 || month > 11 || day < 1 || day > 31) return mmdd;
  return `${day} ${months[month]}`;
}
